import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Layers, Smartphone, Database, Shield, ArrowRight, CheckCircle, Settings, Globe } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ServiceSchema } from '../../components/SchemaMarkup';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveBuildPage: React.FC = () => {
  useSEO('cognitive-build');
  const { isBannerVisible, bannerHeight } = useBanner();

  const features = [
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Full-Stack Development",
      description: "End-to-end application development using modern frameworks like React, Node.js, Python, and cloud-native technologies."
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Mobile Applications",
      description: "Native and cross-platform mobile app development for iOS and Android with seamless user experiences."
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Database Architecture",
      description: "Scalable database design and optimization for high-performance applications with robust data management."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Security First",
      description: "Built-in security measures, encryption, and compliance with industry standards like GDPR and HIPAA."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "API Development",
      description: "RESTful and GraphQL APIs for seamless integration with third-party services and internal systems."
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "DevOps Integration",
      description: "Automated testing, continuous integration, and deployment pipelines for reliable software delivery."
    }
  ];

  const technologies = [
    { category: "Frontend", items: ["React", "Vue.js", "Angular", "TypeScript", "Tailwind CSS"] },
    { category: "Backend", items: ["Node.js", "Python", "Java", "C#", ".NET Core"] },
    { category: "Mobile", items: ["React Native", "Flutter", "Swift", "Kotlin"] },
    { category: "Database", items: ["PostgreSQL", "MongoDB", "Redis", "MySQL", "Firebase"] },
    { category: "Cloud", items: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes"] },
    { category: "AI/ML", items: ["TensorFlow", "PyTorch", "OpenAI", "Hugging Face", "Scikit-learn"] }
  ];

  const benefits = [
    "Reduce development time significantly",
    "Scalable architecture for future growth",
    "Cross-platform compatibility",
    "24/7 monitoring and support",
    "Agile development methodology",
    "Cost-effective solutions",
    "Modern UI/UX design",
    "Performance optimization"
  ];

  const processes = [
    {
      step: 1,
      title: "Requirements Analysis",
      description: "Deep dive into your business needs, technical requirements, and project goals to create detailed specifications."
    },
    {
      step: 2,
      title: "Architecture Design",
      description: "Design scalable, secure, and maintainable architecture using best practices and modern design patterns."
    },
    {
      step: 3,
      title: "Agile Development",
      description: "Iterative development with regular feedback loops, testing, and continuous integration throughout the process."
    },
    {
      step: 4,
      title: "Deployment & Support",
      description: "Seamless deployment to production with ongoing monitoring, maintenance, and feature enhancements."
    }
  ];

  return (
    <>
      <ServiceSchema
        serviceName="Cognitive Build"
        description="Custom software development and web applications built with cutting-edge technologies, scalable architecture, and modern development practices."
        serviceType="Software Development"
        url="https://cognitivecrafthub.com/services/cognitive-build"
        features={[
          "Full-Stack Development",
          "Mobile Applications",
          "Database Architecture",
          "Security First",
          "API Development",
          "Cloud Integration"
        ]}
      />
      <Breadcrumb />
      <main className="pb-20">
        {/* Hero Section */}
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
              <Code className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
              style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
            >
              Cognitive Build
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Custom Software Development Solutions
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Transform your ideas into powerful, scalable software solutions. We build custom applications, 
              web platforms, and mobile apps using cutting-edge technologies and AI-driven development practices.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Development Capabilities</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Comprehensive software development services tailored to your unique business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Technologies We Use</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Cutting-edge tools and frameworks for modern software development
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{tech.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {tech.items.map((item, itemIndex) => (
                    <span key={itemIndex} className="px-3 py-1 bg-blue-500/20 text-blue-300 dark:text-blue-200 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Why Choose Cognitive Build?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Development Process</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Our proven methodology designed for successful project delivery
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{process.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Build Something Amazing?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Let's turn your vision into reality with custom software solutions 
              designed to scale and grow with your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Start Your Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};